import { Component, forwardRef, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';


export interface ChipOption {
  value: string | number;
  label: string;
}

@Component({
  selector: 'app-select-chip',
  standalone: true,
  imports: [],
  templateUrl: './select-chip.component.html',
  styleUrls: ['./select-chip.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectChipComponent),
      multi: true,
    },
  ],
})
export class SelectChipComponent implements ControlValueAccessor {
  // --- Component API ---
  options = input.required<ChipOption[]>();
  multiple = input<boolean>(false);

  // --- Internal State ---
  value: any = null; // Can be a single value or an array
  disabled = false;
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  // Helper to check selection status for both single and multi-select modes
  isSelected(optionValue: string | number): boolean {
    if (this.multiple()) {
      return Array.isArray(this.value) && this.value.includes(optionValue);
    }
    return this.value === optionValue;
  }

  selectOption(optionValue: string | number): void {
    if (this.disabled) return;

    if (this.multiple()) {
      const currentValue = Array.isArray(this.value) ? [...this.value] : [];
      const index = currentValue.indexOf(optionValue);

      if (index > -1) {
        currentValue.splice(index, 1); // Deselect if already selected
      } else {
        currentValue.push(optionValue); // Select if not selected
      }
      this.value = currentValue;
    } else {
      this.value = optionValue; // Single-select mode
    }

    this.onChange(this.value);
    this.onTouched();
  }

  writeValue(value: string | number): void {
    if (this.multiple() && !Array.isArray(value)) {
      this.value = value ? [value] : [];
    } else {
      this.value = value;
    }
  }

  registerOnChange(fn: (value: string | number) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
